/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/camelcase */
import i18n from 'i18n';
import { authenticateFulfilled } from 'modules/authenticate/actions';
import { messagesRequest } from 'modules/messages/actions';
import { modalClose, modalOpen } from 'modules/modal/actions';
import { push } from 'react-router-redux';
import { all, put, takeLatest } from 'redux-saga/effects';
import { exportFileCsv, isRequestOK } from 'utils/helpers';
import { removePayloadKeys } from 'utils/tsHelpers';
import {
  editOperatorFulfilled,
  editOperatorRejected,
  editOperatorRequest,
  editOperatorTokenFulfilled,
  editOperatorTokenRejected,
  editOperatorTokenRequest,
  enableOrDisableMFAOperatorFulfilled,
  enableOrDisableMFAOperatorRejected,
  enableOrDisableMFAOperatorRequest,
  getAllOperatorsFulfilled,
  getAllOperatorsRejected,
  getAllOperatorsRequest,
  getOperatorFulfilled,
  getOperatorRejected,
  getOperatorRequest,
  getOperatorTokensFulfilled,
  getOperatorTokensRejected,
  getOperatorTokensRequest,
  newOperatorFulfilled,
  newOperatorRejected,
  newOperatorRequest,
  newOperatorTokenFulfilled,
  newOperatorTokenRejected,
  newOperatorTokenRequest,
  removeOperatorFulfilled,
  removeOperatorRejected,
  removeOperatorRequest,
  removeOperatorTokenFulfilled,
  removeOperatorTokenRejected,
  removeOperatorTokenRequest,
  resetMFAToOperatorFulfilled,
  resetMFAToOperatorRejected,
  resetMFAToOperatorRequest,
  resetPasswordFulfilled,
  resetPasswordRejected,
  resetPasswordRequest,
  toggleBlockOperatorFulfilled,
  toggleBlockOperatorRejected,
  toggleBlockOperatorRequest,
  updatePermissionsFulfilled,
  updatePermissionsRejected,
  updatePermissionsRequest,
  changeLanguageFulfilled,
  changeLanguageRejected,
  changeLanguageRequest,
  updateMFAFulfilled,
  updateMFARejected,
  updateMFARequest,
  newOperatorAtGroupFulfilled,
  newOperatorAtGroupRejected,
  newOperatorAtGroupRequest,
  getOperatorRolesFulfilled,
  getOperatorRolesRejected,
  getOperatorRolesRequest,
} from './actions';
import endpoints, { api } from './endpoints';
import { GetOperatorRolesRequest } from './requestInterfaces';

function* newOperatorRequestSaga({ payload }) {
  const payloadKeysToRemove = [
    'phone',
    'updated_by_operator',
    'operator_uuid',
    'mobile_phone',
    'updated_date',
    'created_date',
    'created_by_operator',
    'company_uuid',
  ];

  const payloadToRemoveCompanyName = removePayloadKeys(
    payload,
    payloadKeysToRemove
  );

  const {
    company: { company_name, ...updatedCompany },
    ...oldPayload
  } = payloadToRemoveCompanyName;

  const newPayload = {
    ...oldPayload,
    company: updatedCompany,
  };

  try {
    const { status, data } = yield api.post({
      url: endpoints.newOperator(),
      data: {
        ...newPayload,
      },
    });

    if (!isRequestOK(status)) return yield put(newOperatorRejected());

    const redirectTo = `/operators/${data?.operator_uuid}/edit`;
    return yield all([
      put(newOperatorFulfilled()),
      put(
        messagesRequest({
          type: 'success',
          message: i18n.t('OPERATOR_ADDED_SUCCESSFULLY_KEY'),
        })
      ),
      put(push(redirectTo)),
    ]);
  } catch (error) {
    return yield all([put(newOperatorRejected())]);
  }
}

function* newOperatorAtGroupRequestSaga({ payload }) {
  const payloadKeysToRemove = [
    'phone',
    'updated_by_operator',
    'operator_uuid',
    'mobile_phone',
    'updated_date',
    'created_date',
    'created_by_operator',
    'company_uuid',
  ];

  const payloadToRemoveCompanyName = removePayloadKeys(
    payload,
    payloadKeysToRemove
  );

  const {
    company: { company_name, ...updatedCompany },
    ...oldPayload
  } = payloadToRemoveCompanyName;

  const newPayload = {
    ...oldPayload,
    company: updatedCompany,
  };

  try {
    const { status } = yield api.post({
      url: endpoints.newOperator(),
      data: {
        ...newPayload,
      },
    });

    if (!isRequestOK(status)) return yield put(newOperatorAtGroupRejected());

    return yield all([
      put(newOperatorAtGroupFulfilled()),
      put(
        messagesRequest({
          type: 'success',
          message: i18n.t('OPERATOR_ADDED_SUCCESSFULLY_KEY'),
        })
      ),
    ]);
  } catch (error) {
    return yield all([put(newOperatorAtGroupRejected())]);
  }
}

function* changeLanguageRequestSaga({ payload }) {
  try {
    yield api.patch({
      url: endpoints.editOperator(payload.operatorUuid),
      data: { language: payload.lang },
    });

    return yield put(changeLanguageFulfilled());
  } catch (error) {
    return yield put(changeLanguageRejected());
  }
}

function* updatePermissionsRequestSaga({ payload }) {
  try {
    const { data } = yield api.get({
      url: endpoints.updateOperator(),
    });

    return yield all([
      put(updatePermissionsFulfilled()),
      put(authenticateFulfilled({ ...payload, permissions: data.permissions })),
      put(modalClose()),
    ]);
  } catch (error) {
    return yield put(updatePermissionsRejected());
  }
}

function* resetMFAToOperatorRequestSaga({ payload }) {
  const { operator_uuid, query } = payload;
  try {
    const { data } = yield api.post({
      url: endpoints.resetMFAOperator(operator_uuid),
    });

    return yield all([
      put(resetMFAToOperatorFulfilled()),
      put(
        messagesRequest({
          type: 'success',
          message: i18n.t(data.message.code.toUpperCase()),
        })
      ),
      put(getAllOperatorsRequest(query)),
    ]);
  } catch (error) {
    return yield put(resetMFAToOperatorRejected());
  }
}

function* enableOrDisableMFAOperatorRequestSaga({ payload }) {
  const { operator_uuid, query, active } = payload;
  try {
    const { data } = yield api.post({
      url: endpoints.enableOrDisableMFA(operator_uuid),
      data: {
        activate: active,
      },
    });

    return yield all([
      put(enableOrDisableMFAOperatorFulfilled()),
      put(
        messagesRequest({
          type: 'success',
          message: i18n.t(data.message.code.toUpperCase()),
        })
      ),
      put(getAllOperatorsRequest(query)),
    ]);
  } catch (error) {
    return yield put(enableOrDisableMFAOperatorRejected());
  }
}

function* getOperatorTokensRequestSaga({ payload }) {
  const { operatorUuid, query } = payload;

  try {
    const { data } = yield api.get({
      url: endpoints.getOperatorTokens(operatorUuid, query),
    });

    return yield put(getOperatorTokensFulfilled(data));
  } catch (error) {
    return yield put(getOperatorTokensRejected());
  }
}

function* removeOperatorRequestSaga({ payload }) {
  try {
    yield api.delete({
      url: endpoints.removeOperator(payload),
    });

    return yield all([
      put(removeOperatorFulfilled()),
      put(getAllOperatorsRequest()),
      put(
        messagesRequest({
          type: 'success',
          message: i18n.t('OPERATOR_REMOVED_SUCCESSFULLY_KEY'),
        })
      ),
    ]);
  } catch (error) {
    return yield put(removeOperatorRejected());
  }
}

function* editOperatorRequestSaga({ payload }) {
  const { values } = payload;

  const payloadKeysToRemove = [
    'phone',
    'updated_by_operator',
    'operator_uuid',
    'mobile_phone',
    'updated_date',
    'created_date',
    'created_by_operator',
    'company_uuid',
  ];

  const payloadToRemoveCompanyName = removePayloadKeys(
    values,
    payloadKeysToRemove
  );

  const {
    company: { company_name, ...updatedCompany },
    ...oldPayload
  } = payloadToRemoveCompanyName;

  const newPayload = {
    ...oldPayload,
    company: updatedCompany,
  };

  try {
    yield api.put({
      url: endpoints.editOperator(values.operator_uuid),
      data: {
        ...newPayload,
      },
    });

    return yield all([
      put(editOperatorFulfilled()),
      put(modalClose()),
      put(
        messagesRequest({
          type: 'success',
          message: i18n.t('OPERATOR_UPDATED_SUCCESSFULLY_KEY'),
        })
      ),
      put(getOperatorRequest(values.operator_uuid)),
    ]);
  } catch (error) {
    return yield all([put(editOperatorRejected())]);
  }
}

function* getOperatorRequestSaga({ payload }) {
  try {
    const { data } = yield api.get({
      url: endpoints.getOperator(payload),
    });

    return yield put(getOperatorFulfilled(data));
  } catch (error) {
    return yield all([put(getOperatorRejected()), put(push('/operators'))]);
  }
}

function* updateMFARequestSaga({ payload }) {
  const { operatorUuid, mfaChange, isForm } = payload;
  try {
    yield api.patch({
      url: endpoints.editOperator(operatorUuid),
      data: { mfa_required: mfaChange },
    });

    return yield all([
      put(updateMFAFulfilled()),
      put(modalClose()),
      put(
        messagesRequest({
          type: 'success',
          message: mfaChange
            ? i18n.t('MFA_ACTIVATED_SUCCESSFULLY_KEY')
            : i18n.t('MFA_INACTIVATED_SUCCESSFULLY_KEY'),
        })
      ),
      isForm && put(getOperatorRequest(operatorUuid)),
    ]);
  } catch (error) {
    return yield put(updateMFARejected());
  }
}

function* getAllOperatorsRequestSaga({ payload }) {
  try {
    const { data } = yield api.get({
      url: endpoints.getAllOperators(payload),
    });

    return yield put(getAllOperatorsFulfilled(data));
  } catch (error) {
    return yield put(getAllOperatorsRejected());
  }
}

function* resetPasswordRequestSaga({ payload }) {
  try {
    yield api.post({
      url: endpoints.resetPasswordOperator(),
      data: payload,
    });

    return yield all([
      put(
        resetPasswordFulfilled(),
        put(
          messagesRequest({
            type: 'success',
            message: i18n.t('SUCCESS_RESET_PASSWORD'),
          })
        )
      ),
    ]);
  } catch (error) {
    return yield put(resetPasswordRejected());
  }
}

function* toggleBlockOperatorRequestSaga({ payload }) {
  const { operator_uuid, blocked, query } = payload;

  try {
    yield api.patch({
      url: endpoints.editOperator(operator_uuid),
      data: {
        blocked,
      },
    });

    if (query) yield put(getAllOperatorsRequest(query));

    return yield all([
      put(toggleBlockOperatorFulfilled()),
      put(
        messagesRequest({
          type: 'success',
          message: i18n.t(
            blocked
              ? 'OPERATOR_BLOCKED_SUCCESSFULLY_KEY'
              : 'OPERATOR_UNBLOCKED_SUCCESSFULLY_KEY'
          ),
        })
      ),
      put(getOperatorRequest(operator_uuid)),
    ]);
  } catch (error) {
    return yield put(toggleBlockOperatorRejected());
  }
}

function* newOperatorTokenRequestSaga({ payload }) {
  const { operatorUuid, values, query, operatorEmail } = payload;

  try {
    const { data } = yield api.post({
      url: endpoints.newOperatorToken(operatorUuid),
      data: values,
    });

    return yield all([
      put(newOperatorTokenFulfilled()),
      put(
        modalOpen({
          title: i18n.t('CREATED_TOKEN_TITLE_KEY'),
          content: i18n.t(data.message.code.toUpperCase(), {
            token: data.token,
          }),
          triggerFunction: {
            action: () =>
              exportFileCsv(['token', 'email'], [data.token, operatorEmail]),
            label: i18n.t('DOWNLOAD_CSV_TOKEN_KEY'),
          },
        })
      ),
      put(getOperatorTokensRequest({ operatorUuid, query })),
    ]);
  } catch (error) {
    return yield put(newOperatorTokenRejected());
  }
}

function* removeOperatorTokenRequestSaga({ payload }) {
  const { operatorUuid, tokenUuid, query } = payload;

  try {
    yield api.delete({
      url: endpoints.operatorToken(operatorUuid, tokenUuid),
    });

    return yield all([
      put(removeOperatorTokenFulfilled()),
      put(
        messagesRequest({
          type: 'success',
          message: i18n.t('TOKEN_REMOVE_SUCCESS_KEY'),
        })
      ),
      put(getOperatorTokensRequest({ operatorUuid, query })),
    ]);
  } catch (error) {
    return yield put(removeOperatorTokenRejected());
  }
}

function* editOperatorTokenRequestSaga({ payload }) {
  const { operatorUuid, tokenUuid, values, query } = payload;

  try {
    yield api.put({
      url: endpoints.operatorToken(operatorUuid, tokenUuid),
      data: values,
    });

    return yield all([
      put(editOperatorTokenFulfilled()),
      put(
        messagesRequest({
          type: 'success',
          message: i18n.t('OPERATORS_PAGE.TOKEN_UPDATED_SUCCESSFULLY_KEY'),
        })
      ),
      put(getOperatorTokensRequest({ operatorUuid, query })),
    ]);
  } catch (error) {
    return yield put(editOperatorTokenRejected());
  } finally {
    yield put(modalClose());
  }
}

function* getOperatorRolesRequestSaga({ payload }) {
  const {
    operatorUuid,
    showAllRoles = true,
    query,
  }: GetOperatorRolesRequest = payload;

  try {
    const { data } = yield api.get({
      url: endpoints.operatorRole(operatorUuid, showAllRoles, query),
    });

    return yield put(getOperatorRolesFulfilled(data));
  } catch (error) {
    return yield put(getOperatorRolesRejected());
  }
}

export default all([
  takeLatest(newOperatorRequest, newOperatorRequestSaga),
  takeLatest(newOperatorAtGroupRequest, newOperatorAtGroupRequestSaga),
  takeLatest(removeOperatorRequest, removeOperatorRequestSaga),
  takeLatest(editOperatorRequest, editOperatorRequestSaga),
  takeLatest(getOperatorRequest, getOperatorRequestSaga),
  takeLatest(getAllOperatorsRequest, getAllOperatorsRequestSaga),
  takeLatest(toggleBlockOperatorRequest, toggleBlockOperatorRequestSaga),
  takeLatest(resetPasswordRequest, resetPasswordRequestSaga),
  takeLatest(getOperatorTokensRequest, getOperatorTokensRequestSaga),
  takeLatest(newOperatorTokenRequest, newOperatorTokenRequestSaga),
  takeLatest(removeOperatorTokenRequest, removeOperatorTokenRequestSaga),
  takeLatest(editOperatorTokenRequest, editOperatorTokenRequestSaga),
  takeLatest(resetMFAToOperatorRequest, resetMFAToOperatorRequestSaga),
  takeLatest(
    enableOrDisableMFAOperatorRequest,
    enableOrDisableMFAOperatorRequestSaga
  ),
  takeLatest(updatePermissionsRequest, updatePermissionsRequestSaga),
  takeLatest(changeLanguageRequest, changeLanguageRequestSaga),
  takeLatest(updateMFARequest, updateMFARequestSaga),
  takeLatest(getOperatorRolesRequest, getOperatorRolesRequestSaga),
]);
